import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import communicationLogService from '../../../api/services/communicationLogService';
import candidateService from '../../../api/services/candidateService';
import offerService from '../../../api/services/offerService';
import {
  ArrowLeft,
  Paperclip,
  FileText,
  Forward
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { API_BASE_URL } from '../../../api/config';

const ForwardCommunication = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [formData, setFormData] = useState({
    subject: '',
    type: 'email',
    priority: 'medium',
    to: '',
    cc: '',
    bcc: '',
    message: '',
    attachments: [],
    candidateId: '',
    offerId: '',
    scheduledDate: '',
    reminderDate: '',
    originalMessage: '',
    originalSubject: '',
    originalFrom: '',
    originalDate: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [offers, setOffers] = useState([]);
  const [originalCommunication, setOriginalCommunication] = useState(null);
  const nowIsoLocal = new Date().toISOString().slice(0, 16);

  // Communication types and priority levels

  const communicationTypes = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'sms', label: 'SMS' },
    { value: 'video_call', label: 'Video Call' }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: 'text-gray-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  // Filter candidates based on search
  useEffect(() => {
    if (Array.isArray(candidates)) {
      const filtered = candidates.filter(candidate => {
        const name = `${candidate.firstName || ''} ${candidate.lastName || ''}`.toLowerCase();
        const title = (candidate.position || candidate.jobTitle || '').toLowerCase();
        const search = candidateSearch.toLowerCase();
        return !search || name.includes(search) || title.includes(search);
      });
      setFilteredCandidates(filtered);
    }
  }, [candidates, candidateSearch]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCandidateDropdown && !event.target.closest('.candidate-dropdown')) {
        setShowCandidateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCandidateDropdown]);
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load original communication
      const communicationResponse = await communicationLogService.getCommunicationLogById(id);
      
      if (communicationResponse && (communicationResponse.responseCode === 200 || communicationResponse.responseCode === 201)) {
        const commData = communicationResponse.data;
        setOriginalCommunication(commData);
        
        // Set up forward data based on original communication
        setFormData(prev => ({
          ...prev,
          subject: `Fwd: ${commData.subject || 'No Subject'}`,
          type: commData.communicationType || 'email',
          candidateId: commData.candidateId || '',
          offerId: commData.offerId || '',
          attachments: commData.attachments || [],
          originalMessage: commData.message || '',
          originalSubject: commData.subject || '',
          originalFrom: commData.sentBy?.email || commData.sentBy?.firstName ? 
            `${commData.sentBy?.firstName || ''} ${commData.sentBy?.lastName || ''}`.trim() : 
            'Unknown Sender',
          originalDate: commData.sentDate || new Date().toISOString()
        }));
      } else {
        showError('Failed to load original communication. Please try again.');
      }
      
      // Load candidates (real list)
      try {
        const candidatesResponse = await candidateService.getCandidates({ limit: 200 });
        const apiCandidates = (candidatesResponse && (candidatesResponse.data?.candidates || candidatesResponse.data)) || [];
        setCandidates(Array.isArray(apiCandidates) ? apiCandidates : []);
      } catch (candidateError) {
        setCandidates([]);
      }
      
      // Load offers
      try {
        const offersResponse = await offerService.getOffers();
        
        let offersData = [];
        
        if (offersResponse) {
          // Handle different response structures
          if (offersResponse.responseCode === 200 || offersResponse.responseCode === 201) {
            offersData = offersResponse.data?.offers || offersResponse.data || [];
          } else if (Array.isArray(offersResponse)) {
            offersData = offersResponse;
          } else if (offersResponse.data && Array.isArray(offersResponse.data)) {
            offersData = offersResponse.data;
          } else if (offersResponse.offers && Array.isArray(offersResponse.offers)) {
            offersData = offersResponse.offers;
          }
        }
        
        setOffers(Array.isArray(offersData) ? offersData : []);
        
        if (offersData.length === 0) {
          // Use mock data as fallback
          const mockOffers = [
            { _id: '1', title: 'Senior Software Engineer', candidateName: 'John Doe' },
            { _id: '2', title: 'Product Manager', candidateName: 'Jane Smith' },
            { _id: '3', title: 'UX Designer', candidateName: 'Mike Johnson' }
          ];
          setOffers(mockOffers);
        }
      } catch (offerError) {
        // Use mock data as fallback
        const mockOffers = [
          { _id: '1', title: 'Senior Software Engineer', candidateName: 'John Doe' },
          { _id: '2', title: 'Product Manager', candidateName: 'Jane Smith' },
          { _id: '3', title: 'UX Designer', candidateName: 'Mike Johnson' }
        ];
        setOffers(mockOffers);
      }
      
    } catch (error) {
      showError('Error loading data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Live caps and sanitization
    if (name === 'subject') {
      // Only allow alphanumeric, spaces, and commas
      value = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, 150);
    } else if (name === 'message') {
      // Only allow alphanumeric, spaces, and commas
      value = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, 5000);
    } else if (name === 'to' || name === 'cc' || name === 'bcc') {
      const MAX = 254; if (value.length > MAX) value = value.slice(0, MAX);
      value = value.replace(/\s+/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateField = (name, value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let msg = '';
    switch (name) {
      case 'subject':
        if (!value || value.trim().length < 3) msg = 'Subject is required (min 3 characters).';
        else if (!/^[a-zA-Z0-9\s,]+$/.test(value.trim())) msg = 'Subject can only contain alphanumeric characters, spaces, and commas.';
        else if (value.length > 150) msg = 'Subject must be ≤ 150 characters.';
        break;
      case 'to':
        if (!value) msg = 'Recipient is required.';
        else if (!emailRegex.test(value)) msg = 'Enter a valid email address.';
        break;
      case 'cc':
      case 'bcc':
        if (value && !emailRegex.test(value)) msg = 'Enter a valid email address.';
        break;
      case 'message':
        if (value && value.trim().length < 10) msg = 'Message must be at least 10 characters.';
        else if (value && !/^[a-zA-Z0-9\s,]+$/.test(value.trim())) msg = 'Message can only contain alphanumeric characters, spaces, and commas.';
        else if (value && value.length > 5000) msg = 'Message must be ≤ 5000 characters.';
        break;
      case 'scheduledDate': {
        if (value) {
          const selected = new Date(value);
          const now = new Date();
          if (selected.getTime() < now.getTime() - 60000) {
            msg = 'Scheduled date/time cannot be in the past.';
          }
        }
        break;
      }
      case 'reminderDate': {
        if (value) {
          const selected = new Date(value);
          const now = new Date();
          if (selected.getTime() < now.getTime() - 60000) {
            msg = 'Reminder date/time cannot be in the past.';
          }
          if (!msg && formData.scheduledDate) {
            const scheduled = new Date(formData.scheduledDate);
            if (selected.getTime() < scheduled.getTime() - 60000) {
              msg = 'Reminder cannot be before the scheduled date/time.';
            }
          }
        }
        break;
      }
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: msg }));
    return !msg;
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const processedFiles = files.map(file => ({
      name: file.name,
      type: file.type,
      size: file.size,
      file: file // Keep the actual file for upload
    }));
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...processedFiles]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const fields = ['subject','to','cc','bcc','message','scheduledDate','reminderDate'];
    let ok = true;
    fields.forEach(f => { if (!validateField(f, formData[f])) ok = false; });
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Upload attachments to Cloudinary first
      let uploadedAttachments = [];
      if (formData.attachments && formData.attachments.length > 0) {
        for (const attachment of formData.attachments) {
          if (attachment.file) {
            // Upload new file to Cloudinary
            const formData = new FormData();
            formData.append('file', attachment.file);
            formData.append('folder', 'communication-attachments');
            
            const uploadResponse = await authFetch(`${API_BASE_URL}/upload/cloudinary`, {
        credentials: 'include',
              method: 'POST',
              
              body: formData
            });
            
            const uploadResult = await uploadResponse.json();
            
            if (uploadResult.responseCode === 200) {
              uploadedAttachments.push({
                name: attachment.name,
                url: uploadResult.data?.url || uploadResult.data?.secure_url || uploadResult.url || uploadResult.secure_url,
                size: attachment.size,
                type: attachment.type
              });
            } else {
              showError(`Failed to upload ${attachment.name}. Please try again.`);
              return;
            }
          } else if (attachment.url) {
            // Already uploaded attachment
            uploadedAttachments.push({
              name: attachment.name,
              url: attachment.url,
              size: attachment.size,
              type: attachment.type
            });
          }
        }
      }

      // Prepare data for API
      const forwardData = {
        subject: formData.subject,
        message: formData.message,
        recipient: formData.to,
        cc: formData.cc,
        bcc: formData.bcc,
        priority: formData.priority,
        communicationType: formData.type,
        candidateId: formData.candidateId,
        offerId: formData.offerId,
        attachments: uploadedAttachments,
        scheduledDate: formData.scheduledDate,
        reminderDate: formData.reminderDate
      };

      const response = await communicationLogService.forwardCommunication(id, forwardData);
      
      if (response && (response.responseCode === 200 || response.responseCode === 201)) {
        showSuccess('Communication forwarded successfully!');
        setTimeout(() => {
          navigate('/offer_communication_logs');
        }, 1500);
      } else {
        showError(response?.responseMessage || 'Failed to forward communication. Please try again.');
      }
    } catch (error) {
      showError('Error forwarding communication. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/offer_communication_logs');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading communication data...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the original message and related data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleCancel}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Forward Communication</h1>
            <p className="text-gray-600 mt-1">Forward offer-related communication</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Forward className="h-4 w-4" />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
          >
            {isSubmitting ? 'Forwarding...' : 'Forward Communication'}
          </Button>
        </div>
      </div>

      {/* Original Message */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Original Message</h3>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                From: {formData.originalFrom || 'Unknown Sender'}
              </span>
              <span className="text-sm text-gray-500">
                {formData.originalDate ? new Date(formData.originalDate).toLocaleString() : 'Unknown Date'}
              </span>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {formData.originalSubject || 'No Subject'}
            </h4>
          </div>
          <div className="text-gray-700 whitespace-pre-wrap">
            {formData.originalMessage || 'No message content available'}
          </div>
          {formData.attachments && formData.attachments.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Original Attachments:</h5>
              <div className="space-y-1">
                {formData.attachments.map((file, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                    <Paperclip className="h-3 w-3" />
                    <span>
                      {typeof file === 'string' ? file : file.name} 
                      {file.size && ` (${Math.round(file.size / 1024)}KB)`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Debug Information removed */}

      {/* Forward Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e)=>validateField('subject', e.target.value)}
                maxLength={150}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.subject ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter forward subject"
                required
              />
              <div className="flex justify-between items-center mt-1">
                {errors.subject && (
                  <p className="text-sm text-red-600">{errors.subject}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.subject?.length || 0}/150 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {communicationTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Recipient Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To *
              </label>
              <input
                type="text"
                name="to"
                value={formData.to}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('to', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.to ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter recipient email or phone"
              />
              {errors.to && (
                <p className="mt-1 text-sm text-red-600">{errors.to}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {priorityLevels.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* CC and BCC */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC
              </label>
              <input
                type="text"
                name="cc"
                value={formData.cc}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('cc', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter CC recipients"
              />
              {errors.cc && (
                <p className="mt-1 text-sm text-red-600">{errors.cc}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                BCC
              </label>
              <input
                type="text"
                name="bcc"
                value={formData.bcc}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('bcc', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter BCC recipients"
              />
              {errors.bcc && (
                <p className="mt-1 text-sm text-red-600">{errors.bcc}</p>
              )}
            </div>
          </div>

      {/* Related Records */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Candidate
              </label>
              <div className="relative candidate-dropdown">
                <input
                  type="text"
                  value={candidateSearch}
                  maxLength={100}
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z0-9\s]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Only allow alphanumeric characters and spaces
                    value = value.replace(/[^a-zA-Z0-9\s]/g, '');
                    // Enforce max length
                    if (value.length > 100) {
                      value = value.substring(0, 100);
                    }
                    setCandidateSearch(value);
                    setShowCandidateDropdown(true);
                  }}
                  onFocus={() => setShowCandidateDropdown(true)}
                  placeholder="Search and select candidate..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                {showCandidateDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredCandidates.length > 0 ? (
                      filteredCandidates.map(candidate => (
                        <div
                          key={candidate._id || candidate.id}
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              candidateId: candidate._id || candidate.id
                            }));
                            setCandidateSearch(`${candidate.firstName} ${candidate.lastName} - ${candidate.position || candidate.jobTitle || 'No Position'}`);
                            setShowCandidateDropdown(false);
                          }}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium">{candidate.firstName} {candidate.lastName}</div>
                          <div className="text-sm text-gray-500">{candidate.position || candidate.jobTitle || 'No Position'}</div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500">No candidates found</div>
                    )}
                  </div>
                )}
              </div>
              {isLoading && (
                <p className="mt-1 text-sm text-gray-500">Loading candidates...</p>
              )}
              {!isLoading && Array.isArray(candidates) && candidates.length === 0 && (
                <p className="mt-1 text-sm text-red-500">No candidates available</p>
              )}
              {!isLoading && Array.isArray(candidates) && candidates.length > 0 && (
                <p className="mt-1 text-sm text-green-600">
                  {candidateSearch ? `${filteredCandidates.length} of ${candidates.length} candidates` : `${candidates.length} candidates loaded`}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Offer
              </label>
              <select
                name="offerId"
                value={formData.offerId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              >
                <option value="">Select an offer</option>
                {Array.isArray(offers) && offers.map(offer => (
                  <option key={offer._id || offer.id} value={offer._id || offer.id}>
                    {offer.title || offer.jobTitle} - {offer.candidateName || `${offer.candidate?.firstName || ''} ${offer.candidate?.lastName || ''}`.trim() || 'No Candidate'}
                  </option>
                ))}
              </select>
              {isLoading && (
                <p className="mt-1 text-sm text-gray-500">Loading offers...</p>
              )}
              {!isLoading && (!Array.isArray(offers) || offers.length === 0) && (
                <p className="mt-1 text-sm text-red-500">No offers available</p>
              )}
              {!isLoading && Array.isArray(offers) && offers.length > 0 && (
                <p className="mt-1 text-sm text-green-600">{offers.length} offers loaded</p>
              )}
            </div>
          </div>

          {/* Forward Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Forward Message (Optional)
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Only allow alphanumeric, spaces, and commas
                if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onBlur={(e)=>validateField('message', e.target.value)}
              maxLength={5000}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add a note to accompany the forwarded message..."
            />
            <div className="flex justify-between items-center mt-1">
              {errors.message && (
                <p className="text-sm text-red-600">{errors.message}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.message?.length || 0}/5000 characters
              </p>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Paperclip className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop files here, or click to select
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Add Files
                </label>
              </div>
              
              {formData.attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attached Files:</h4>
                  <div className="space-y-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">
                          {file.name} {file.size && `(${Math.round(file.size / 1024)}KB)`}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scheduling */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scheduled Date
              </label>
              <input
                type="datetime-local"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('scheduledDate', e.target.value)}
                min={nowIsoLocal}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.scheduledDate && (
                <p className="mt-1 text-sm text-red-600">{errors.scheduledDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Date
              </label>
              <input
                type="datetime-local"
                name="reminderDate"
                value={formData.reminderDate}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('reminderDate', e.target.value)}
                min={nowIsoLocal}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.reminderDate && (
                <p className="mt-1 text-sm text-red-600">{errors.reminderDate}</p>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Custom Alert */}
      <CustomAlert
        show={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default ForwardCommunication;
